import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import { invalidateAuthorizedFile } from '@/utils/file-links';
import { generateSearchParams } from '@/utils/generate-search-params';

import type {
  CreateUpdateServiceProvidersFormValues,
  PortsFilters,
  PortsList,
  PortsListDto,
  ServiceProvidersFilters,
  ServiceProvidersList,
  SingleServiceProvider,
} from './service-providers.dto';
import {
  mapCreateUpdateServiceProviderValues,
  mapServiceProvidersFilters,
  mapServiceProviderDto,
  mapPortsFilters,
  mapPortsList,
} from './service-providers.mappers';

export const createServiceProvider = async (
  vesselId: string,
  values: CreateUpdateServiceProvidersFormValues,
) => {
  const { data } = await postRequest<SingleServiceProvider, unknown>(
    API.SERVICE_PROVIDERS,
    `service-provider?vessel_id=${vesselId}`,
    {
      ...mapCreateUpdateServiceProviderValues(values),
    },
  );
  return data;
};

export interface ServiceProviderLocationBody {
  location: {
    name: string;
    address: null | string;
    latitude: number;
    longitude: number;
    city?: null | string;
    place_id?: null | string;
    state?: null | string;
    zip_code?: null | string;
  };
  service_category?: string[];
  certifications?: string[];
}

export const addServiceProviderLocation = async (id: string, body: ServiceProviderLocationBody) => {
  const { data } = await postRequest(
    API.SERVICE_PROVIDERS,
    `service-provider/${id}/locations`,
    body,
  );
  return data;
};

/**
 * Categories, certifications and the address are *not* on the provider: `PUT /service-provider/:id`
 * binds only name/short_description/description/contact_info, so anything sent alongside them is
 * dropped without an error. They live on `service_provider_locations`, and this endpoint replaces
 * the whole row — every field the body omits is wiped, so resend the ones that should survive.
 */
export const updateServiceProviderLocation = async (
  id: string,
  locationId: string,
  body: ServiceProviderLocationBody,
) => {
  const { data } = await putRequest(
    API.SERVICE_PROVIDERS,
    `service-provider/${id}/locations/${locationId}`,
    body,
  );
  return data;
};

export const setServerProviderLogo = async (id: string, image: File) => {
  const formData = new FormData();
  formData.append('logo', image);
  await patchRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/logo`, formData);
  invalidateAuthorizedFile();
};

export const uploadServiceProviderImages = async (id: string, files: File[]) => {
  const formData = new FormData();
  files.forEach((f) => formData.append('images', f));
  await patchRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/images`, formData);
};

export const deleteServiceProviderImage = async (id: string, imageId: string) => {
  await deleteRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/image`, {
    id: imageId,
  });
};

export const getServiceProviders = async (
  limit: number,
  page: number,
  filters: ServiceProvidersFilters,
): Promise<ServiceProvidersList> => {
  const params = {
    limit,
    page,
    ...mapServiceProvidersFilters(filters),
  };
  const { data } = await getRequest<ServiceProvidersList>(
    API.SERVICE_PROVIDERS,
    `service-provider?${generateSearchParams(params)}`,
  );
  return { ...data, items: data.items.map(mapServiceProviderDto) };
};

export const getSingleServiceProvider = async (
  id: string,
  vesselId?: string,
  current_lat?: number,
  current_lon?: number,
) => {
  const res = await getRequest<SingleServiceProvider>(
    API.SERVICE_PROVIDERS,
    `service-provider/${id}?${generateSearchParams({ vessel_id: vesselId, current_lat, current_lon })}`,
  );
  return mapServiceProviderDto(res.data);
};

export const updateServiceProvider = async (
  id: string,
  vesselId: string,
  values: CreateUpdateServiceProvidersFormValues,
) => {
  const { data } = await putRequest<SingleServiceProvider, unknown>(
    API.SERVICE_PROVIDERS,
    `service-provider/${id}?vessel_id=${vesselId}`,
    {
      ...mapCreateUpdateServiceProviderValues(values),
    },
  );
  return mapServiceProviderDto(data);
};

export const updateServiceProviderFavorite = async (
  id: string,
  vesselId: string,
  favorite: boolean,
) => {
  await patchRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/favorite`, {
    vessel_id: vesselId,
    favorite,
  });
};

export const updateServiceProviderNote = async (id: string, vesselId: string, note: string) => {
  await patchRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/note`, {
    vessel_id: vesselId,
    note,
  });
};

export const deleteServiceProviderNote = async (id: string, vesselId: string) => {
  await deleteRequest(API.SERVICE_PROVIDERS, `service-provider/${id}/note`, {
    vessel_id: vesselId,
  });
};

export const deleteServiceProvider = async (id: string) => {
  await deleteRequest(API.SERVICE_PROVIDERS, `service-provider/${id}`);
};

export const getPorts = async (
  limit: number,
  page: number,
  filters: PortsFilters,
): Promise<PortsList> => {
  const params = {
    limit,
    page,
    ...mapPortsFilters(filters),
  };
  const { data } = await getRequest<PortsListDto>(
    API.SERVICE_PROVIDERS,
    `service-provider/ports?${generateSearchParams(params)}`,
  );
  return mapPortsList(data);
};
